import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import StoreFunctions from '../../../stores/storeFunctions'
import DiamondIcon from 'icons/DiamondIcon'
import RingIcon from 'icons/RingIcon'
import HeartIcon from 'icons/HeartIcon'
import { JewelryType } from 'constants/jewelryTypes'
const storeFunctions = new StoreFunctions()

interface JourneyBarProps {
  activeStep: number
  mode: string
  selectedDiamonds?: { [key: string]: any }[]
  variantId?: string | null
}

const StepZero: React.FC<{jType: string}> = ({ jType }) => {
  const { t } = useTranslation()
  return (
    <>
      <h3>
        <span>{t("journeyBar.designYour", { jType })}</span>
      </h3>
      <svg width="10" height="14" viewBox="0 0 10 14" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 6.99948L0.249999 13.3792L0.25 0.619758L10 6.99948Z" />
      </svg>
    </>
  )
};

interface ChooseDiamondProps {
  step: number
  selectedDiamonds?: { [key: string]: any }[]
}

const ChooseDiamond: React.FC<ChooseDiamondProps> = ({ step, selectedDiamonds = [] }) => {
  const { t } = useTranslation()
  const firstDiamond = selectedDiamonds[0]
  const hasSelectedDiamond = Boolean(firstDiamond)

  return (
    <>
      <div className="unbridaled-step-title-wrapper">
        <span className="unbridaled-accent">{step}</span>
        <h3>
          <span>
            {!hasSelectedDiamond ? (
              <>{t("journeyBar.chooseDiamond")}</>
            ) : (
              <>
                {t("journeyBar.diamond")}
                <span className="unbridaled-diamond-options">
                  <span className="unbridaled-diamond-link unbridaled-diamond-view-link">
                    <a href={`${window.location.origin}/apps/ub/diamond/${firstDiamond?.sku ?? ''}`}>{t("journeyBar.view")}</a>
                  </span>
                  <span className="unbridaled-diamond-link unbridaled-diamond-back-link">
                    <a href={`${window.location.origin}/apps/ub/diamonds`}>{t("journeyBar.change")}</a>
                  </span>
                </span>
              </>
            )}
          </span>
          {selectedDiamonds?.map((diamond) => {
            const originText = origin[diamond.type];
            return (
              <span key={diamond.sku} className="unbridaled-diamond-title">
                {parseFloat(diamond.weight).toFixed(2)}ct {originText} {diamond.shape} {diamond.color}/{diamond.clarity}
              </span>
            );
          })}
        </h3>
      </div>
      {hasSelectedDiamond ? (
        ((): React.ReactNode => {
          if (!firstDiamond) return null
          const shapeIcon = storeFunctions.translateShapeAPINameToIcon(firstDiamond) || "";
          return firstDiamond?.shape ? (
            <div className="unbridaled-diamond-icon" key={firstDiamond.sku} dangerouslySetInnerHTML={{ __html: shapeIcon }} />
          ) : null;
        })()
      ) : (
        <DiamondIcon />
      )}
    </>
  );
}

const ChooseSetting: React.FC<{step: number; selectedVariantId?: string | null}> = ({ step, selectedVariantId = null }) => {
  const { t } = useTranslation()
  const [selectedVariant, setSelectedVariant] = useState<any>(null);

  useEffect(() => {
    const fetchVariant = async (): Promise<void> => {
      if (!selectedVariantId) {
        return;
      }
      try {
        const variant = await storeFunctions.getVariantById(selectedVariantId);
        setSelectedVariant(variant);
      } catch (error) {
        console.error("Error fetching variant:", error);
      }
    };
    fetchVariant();
  }, [selectedVariantId]);
  return (
    <>
      <div className="unbridaled-step-title-wrapper">
        <span className="unbridaled-accent">{step}</span>
        <h3>
          <span>
            {!selectedVariant ? (
              <>{t("journeyBar.chooseSetting")}</>
            ) : (
              <>
                <span className="unbridaled-setting-options">{t("journeyBar.setting")}</span>
                <span className="unbridaled-diamond-link unbridaled-diamond-reset-link">
                  <a href={`${window.location.origin}/apps/ub/diamonds`}>{t("journeyBar.reset")}</a>
                </span>
                <span className='unbridaled-setting-title'>
                  {selectedVariant?.product?.title}
                </span>
              </>
            )}
          </span>
        </h3>

      </div>
      {selectedVariant?.image?.url ? (
        ((): React.ReactNode => {
          return (
            <div className="unbridaled-diamond-icon">
              <img src={selectedVariant.image.url} />
            </div>
          );
        })()
      ) : (
        <RingIcon />
      )}
    </>
  );
}

const CompleteSetting: React.FC<{ jType: string }> = ({ jType }) => {
  const { t } = useTranslation()
  return (
    <>
      <div className="unbridaled-step-title-wrapper">
        <span className="unbridaled-accent">3</span>
        <h3>
          <span>{t("journeyBar.completeSetting", { jType })}</span>
        </h3>
      </div>
      <HeartIcon />
    </>
  );
};

const StepWrapper: React.FC<{ children: React.ReactNode; step: number; activeStep: number }> = ({ children, step, activeStep }) => {
  return <div className={`unbridaled-journey-bar-step unbridaled-step-${step} ${activeStep == step ? "active" : ""}`}>
    <div className="unbridaled-step-wrapper">{children}</div></div>;
};

interface JourneyBarContentProps {
  activeStep: number
  selectedDiamonds?: { [key: string]: any }[]
  selectedVariant?: string | null
  mode: string
}

const JourneyBarContent: React.FC<JourneyBarContentProps> = ({
  activeStep,
  selectedDiamonds = [],
  selectedVariant,
  mode
}) => {
  const jTypeMap: Record<JewelryType, string> = {
    [JewelryType.Ring]: 'ring',
    [JewelryType.Pendant]: 'pendant',
    [JewelryType.Bracelet]: 'bracelet',
    [JewelryType.Earring]: 'earring'
  }
  const jTypeKey = jTypeMap[mode as JewelryType] || 'jewelry'

  const { t } = useTranslation()
  const translatedType = t(`types.${jTypeKey}`, jTypeKey);

  return (
    <div className="unbridaled-journey-bar-wrapper">
      <div className="unbridaled-journey-bar">
        <div className="unbridaled-journey-bar-way">
          <StepWrapper step={0} activeStep={activeStep}>
            <StepZero jType={translatedType} />
          </StepWrapper>
          <StepWrapper step={1} activeStep={activeStep}>
            {selectedVariant ? (
              <ChooseSetting step={1} selectedVariantId={selectedVariant} />
            ) : (
              <ChooseDiamond step={1} selectedDiamonds={selectedDiamonds}/>
            )}
          </StepWrapper>
          <StepWrapper step={2} activeStep={activeStep}>
            {selectedVariant ? (
              <ChooseDiamond step={2} selectedDiamonds={selectedDiamonds}/>
            ) : (
              <ChooseSetting step={2}/>
            )}
          </StepWrapper>
          <StepWrapper step={3} activeStep={activeStep}>
            <CompleteSetting jType={translatedType} />
          </StepWrapper>
        </div>
      </div>
    </div>
  );
};

export const JourneyBar: React.FC<JourneyBarProps> = ({ activeStep, mode, selectedDiamonds = [], variantId = null }) => {
  return (
    <JourneyBarContent
      activeStep={activeStep}
      selectedDiamonds={selectedDiamonds}
      selectedVariant={variantId}
      mode={mode}
    />
  )
}

export default JourneyBar
